/**
 * ListRow — shared aspect. The canonical row treatment for the app's
 * lists and tables: an optional leading slot (avatar / status dot), a
 * title + optional subtitle, an optional right-aligned meta line, and an
 * optional trailing slot (status pill / action). Rows live inside a
 * `ListRowGroup` card which draws the surface, border, and hairline
 * dividers between rows.
 *
 * Single source of truth: /brand Aspect 09 renders this same component,
 * so editing here updates the reference AND every real list (admin CRM,
 * clients, students, judges, inquiries, invites, …).
 *
 * Theme tokens consumed: surfaceBg, surfaceRaisedBg, border, divider,
 * accentBorderHover, neutralHoverBgOnDark, textOnSurface,
 * textOnSurfaceDim, textOnSurfaceFaint, body/medium fonts.
 */
import type { ReactNode } from "react";
import { Platform, Pressable, Text, View, type ViewStyle } from "react-native";

import { RADII, SPACING, STROKES } from "../../lib/brand";
import type { PaneTheme } from "../brand/paneThemes";

export type ListRowGroupProps = {
  theme: PaneTheme;
  children: ReactNode;
  /** Optional outer style override (e.g. marginTop). */
  style?: ViewStyle;
};

/**
 * Card container for a run of <ListRow/>s. Draws the surface + border and
 * clips the hairline dividers the rows render on their top edge.
 */
export function ListRowGroup({ theme, children, style }: ListRowGroupProps) {
  return (
    <View
      style={[
        {
          backgroundColor: theme.surfaceBg,
          borderWidth: STROKES.hairline,
          borderColor: theme.border,
          borderRadius: RADII.md,
          overflow: "hidden",
        },
        style ?? null,
      ]}
    >
      {children}
    </View>
  );
}

export type ListRowProps = {
  theme: PaneTheme;
  /** Primary label. */
  title: ReactNode;
  /** Secondary line under the title. */
  subtitle?: ReactNode;
  /** Right-aligned meta (e.g. a count, date, or relative time). */
  meta?: ReactNode;
  /** Leading slot — avatar, status dot, icon. */
  leading?: ReactNode;
  /** Trailing slot — status pill, chevron, inline action. */
  trailing?: ReactNode;
  /** Makes the row a Pressable with hover tint on web. */
  onPress?: () => void;
  /** First row in a group skips its top divider. Default false. */
  first?: boolean;
  accessibilityLabel?: string;
};

export function ListRow({
  theme,
  title,
  subtitle,
  meta,
  leading,
  trailing,
  onPress,
  first = false,
  accessibilityLabel,
}: ListRowProps) {
  const isWeb = Platform.OS === "web";

  const body = (hovered: boolean) => (
    <View
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: SPACING.sm,
          paddingVertical: 13,
          paddingHorizontal: SPACING.md,
          borderTopWidth: first ? 0 : STROKES.hairline,
          borderTopColor: theme.divider,
          backgroundColor:
            hovered && isWeb && onPress
              ? theme.neutralHoverBgOnDark
              : "transparent",
        },
        isWeb && onPress
          ? ({ transition: "background-color 0.15s ease" } as unknown as ViewStyle)
          : null,
      ]}
    >
      {leading != null ? leading : null}
      <View style={{ flex: 1, minWidth: 0 }}>
        {typeof title === "string" ? (
          <Text
            numberOfLines={1}
            style={{
              fontFamily: theme.bodyMediumFont,
              fontSize: 13,
              color: theme.textOnSurface,
            }}
          >
            {title}
          </Text>
        ) : (
          title
        )}
        {subtitle != null ? (
          typeof subtitle === "string" ? (
            <Text
              numberOfLines={1}
              style={{
                marginTop: 1,
                fontFamily: theme.bodyFont,
                fontSize: 11.5,
                color: theme.textOnSurfaceDim,
              }}
            >
              {subtitle}
            </Text>
          ) : (
            subtitle
          )
        ) : null}
      </View>
      {meta != null ? (
        typeof meta === "string" ? (
          <Text
            style={{
              fontFamily: theme.bodyFont,
              fontSize: 11.5,
              color: theme.textOnSurfaceFaint,
            }}
          >
            {meta}
          </Text>
        ) : (
          meta
        )
      ) : null}
      {trailing != null ? trailing : null}
    </View>
  );

  if (!onPress) return body(false);

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={
        isWeb ? ({ cursor: "pointer" } as unknown as ViewStyle) : undefined
      }
    >
      {({ hovered }: { hovered?: boolean }) => body(!!hovered)}
    </Pressable>
  );
}
